import { NoDataPage } from '@/src/components';
import Breadcrumb from '@/src/components/Antd/Breadcrumb/Breadcrumb';
import { MenuItemType } from '@/src/components/BulkEditor/BulkEditor';
import GlobalTable from '@/src/constrains/BulkEditorTable/BulkEditorTable2';
import {
  reportSubscriptionDelete,
  reportSubscriptionList,
} from '@/src/request/analytics/reportSubscription';
import { NEW } from '@/src/tools/constant';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { useQueries, useSelectRows } from '@/src/tools/hooks/listPageHooks';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { message } from 'antd';
import React, { memo, useState } from 'react';
import { useHistory } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import ListPageLayout from 'src/layout/ListPageLayout/ListPageLayout';
import { defaultPageSize } from 'src/tools/constant';
import { useAfterDidMountEffect } from 'src/tools/customHooks';
import { randomNumber } from 'src/tools/helper';
import { i18nGet } from 'src/tools/tools';
import { breadcrumbData, columns } from './config';
import css from './css.module.less';

const permissions = () => {
  const view_report_subscription_listing_page = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.view_report_subscription_listing_page,
      }),
    ],
  });
  const view_report_subscription_details_page = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.view_report_subscription_details_page,
      }),
    ],
  });
  const add_edit_report_subscription = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.add_edit_report_subscription,
      }),
    ],
  });
  const delete_report_subscription = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Analytics,
        subCategory: EnumSubCategory.AnalyticsReportSubscription,
        operation: EnumOperation.delete_report_subscription,
      }),
    ],
  });
  return {
    view_report_subscription_listing_page,
    view_report_subscription_details_page,
    add_edit_report_subscription,
    delete_report_subscription,
  };
};

const ReportSubscriptionList = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    view_report_subscription_listing_page,
    add_edit_report_subscription,
    delete_report_subscription,
  } = permissions();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  /* 分页 */
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [timezoneInGMT, setTimezoneInGMT] = useState('');
  const [noProducts, setNoProducts] = useState(false);
  const [sortBy, setSortBy] = useState<any>({});
  const [tabsActive, setTabsActive] = useState('');
  //分页配置
  const paginationProps = {
    defaultPageSize: pageSize,
    showQuickJumper: true,
  };

  const { clearSearchState, queryProps, search } = useQueries([], []);

  /* 多选 */
  const {
    rowSelection,
    selectedRowKeys,
    setMappedSelectedKeys,
    //mappedSelectedKeys,
    setSelectedRowKeys,
  } = useSelectRows(orders);

  //清除table状态
  const clearTableState = () => {
    setSelectedRowKeys([]);
    setMappedSelectedKeys([]);
  };

  //tab改变事件
  const tabChange = (activeKey) => {
    clearTableState();
    clearSearchState();
    setCurrent(0);
    setTabsActive(activeKey);
  };

  const getProduct = (cb) => {
    if (!loading) {
      setLoading(true);
    }
    const data = search;

    const _data = {
      ...data,
      sortBy,
    };
    reportSubscriptionList(current * pageSize, pageSize, _data).then((res) => {
      const { totalCount } = res;
      setTotal(totalCount);
      const orders = res.results.map((i) => {
        return {
          ...i,
          key: randomNumber(),
        };
      });
      setOrders(orders);
      setLoading(false);
    });
  };

  const buttonMenu = [
    //Bulk Edit
    {
      key: 1,
      value: getI18n('general.delete'),
      type: MenuItemType.Error,
      function: () => handleDelete(selectedRowKeys),
      disabled: !delete_report_subscription,
    },
  ];

  React.useEffect(() => {
    if (view_report_subscription_listing_page) {
      const data = {};
      reportSubscriptionList(current * pageSize, pageSize, data).then((res) => {
        const {
          extraInfo: { timeZone = '' } = {},
          totalCount = 0,
          results = [],
        } = res;
        setTimezoneInGMT(timeZone);
        setTotal(totalCount);
        setOrders(results);
        if (totalCount > 0) {
          setNoProducts(true);
        }
      });
    }
  }, []);

  useAfterDidMountEffect(() => {
    if (view_report_subscription_listing_page) {
      getProduct(() => void 0);
    }
  }, [tabsActive, current]);

  useAfterDidMountEffect(() => {
    if (view_report_subscription_listing_page) {
      if (!current) {
        getProduct(() => void 0);
      } else {
        setCurrent(0);
      }
    }
  }, [pageSize, search, sortBy]);

  const handleGoToEdit = (uuid?) => {
    history.push(`/reportSubscription/${uuid ? uuid : NEW}`);
  };
  const pageLayoutProps = {
    title: getI18n('reportSubscription.reportSubscriptionList.title'),
    tabChange,
    activeKey: tabsActive,
    tabsConfig: [
      //定义tabs列表
      { name: getI18n('general.all'), key: '' },
    ],
    addFun: () => handleGoToEdit(),
    addText: getI18n('reportSubscription.reportSubscriptionList.addtext'),
    disableAdd: !add_edit_report_subscription,
  };

  const handleDelete = (uuids) => {
    reportSubscriptionDelete({ uuids }).then(() => {
      message.success(
        getI18n('reportSubscription.reportSubscriptionList.msgsucc'),
      );
      getProduct(() => void 0);
    });
  };
  return (
    <React.Fragment>
      <Breadcrumb
        className={css.breadcrumb}
        breadcrumbData={breadcrumbData({ getI18n })}></Breadcrumb>
      {!noProducts ? (
        <NoDataPage
          title={getI18n(
            'reportSubscription.reportSubscriptionList.nodatapage_title',
          )}
          introduce={getI18n(
            'reportSubscription.reportSubscriptionList.nodatapage_introduce',
          )}
          buttonTitle={getI18n(
            'reportSubscription.reportSubscriptionList.nodatapage_buttonTitle',
          )}
          onClick={() => handleGoToEdit()}
          disabled={!add_edit_report_subscription}
        />
      ) : (
        <ListPageLayout {...pageLayoutProps} tabChange={tabChange}>
          <GlobalTable
            id='reportSubscriptionList'
            queryProps={queryProps}
            dataSource={orders}
            columns={columns(
              timezoneInGMT,
              handleDelete,
              handleGoToEdit,
              !add_edit_report_subscription,
              !delete_report_subscription,
              { getI18n },
            )}
            rowKey='uuid'
            pagination={paginationProps}
            current={current}
            setCurrent={setCurrent}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={total}
            buttonMenu={buttonMenu}
            holdHeader
            rowSelection={rowSelection}
            totalText={getI18n(
              'reportSubscription.reportSubscriptionList.displayingsubscription',
            )}
            selectedRowKeys={selectedRowKeys}
            hidePagination={total == 0}
            onChange={(_, s, sort) => {
              const ascent = sort.order == 'ascend' ? true : false;
              const field = sort.field;
              setSortBy({ field, ascent });
            }}
            loading={loading}
          />
        </ListPageLayout>
      )}
    </React.Fragment>
  );
};

export default memo(ReportSubscriptionList);
